import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import MultiSelectFilter from '../components/MultiSelectFilter';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Eye, Loader2, ClipboardCheck, FileText, Link2, Repeat, Ruler, Leaf, CheckCircle2, XCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { getCategoryLabel, getCountryName } from '../lib/constants';
import { useAuth } from '../contexts/AuthContext';
import { reloadPortalStartupMaps } from '../lib/portalApi';
import { formatFactorValue } from '../lib/format';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PendingApprovalsPage() {
  const [factors, setFactors] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [unitConversions, setUnitConversions] = useState([]);
  const [lcaFactors, setLcaFactors] = useState([]);
  const [lcaCreatedByFilter, setLcaCreatedByFilter] = useState([]);
  const [lcaDateFrom, setLcaDateFrom] = useState('');
  const [lcaDateTo, setLcaDateTo] = useState('');
  const [loadingFactors, setLoadingFactors] = useState(true);
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [loadingConversions, setLoadingConversions] = useState(true);
  const [loadingUnitConversions, setLoadingUnitConversions] = useState(true);
  const [loadingLcaFactors, setLoadingLcaFactors] = useState(true);
  const [activeTab, setActiveTab] = useState('emission-factors');
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'approve' | 'reject' | null
  const [bulkComment, setBulkComment] = useState('');
  const [processingBulk, setProcessingBulk] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const canApprove = user && (user.role === 'admin' || user.role === 'approver');

  useEffect(() => {
    fetchPendingFactors();
    fetchPendingMappings();
    fetchPendingConversions();
    fetchPendingUnitConversions();
    fetchPendingLcaFactors();
  }, []);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkResults(null);
  }, [activeTab]);

  const fetchPendingFactors = async () => {
    setLoadingFactors(true);
    try {
      const response = await axios.get(`${API_URL}/pending-approvals`);
      setFactors(response.data);
    } catch (error) {
      toast.error('Failed to load pending emission factors');
    } finally {
      setLoadingFactors(false);
    }
  };

  const fetchPendingMappings = async () => {
    setLoadingMappings(true);
    try {
      const response = await axios.get(`${API_URL}/custom-mappings/pending`);
      setMappings(response.data);
    } catch (error) {
      toast.error('Failed to load pending custom mappings');
    } finally {
      setLoadingMappings(false);
    }
  };

  const fetchPendingConversions = async () => {
    setLoadingConversions(true);
    try {
      const response = await axios.get(`${API_URL}/category-conversions?status=pending&page_size=200`);
      setConversions(response.data.items);
    } catch (error) {
      toast.error('Failed to load pending category conversions');
    } finally {
      setLoadingConversions(false);
    }
  };

  const fetchPendingUnitConversions = async () => {
    setLoadingUnitConversions(true);
    try {
      const response = await axios.get(`${API_URL}/unit-conversions?status=pending&page_size=200`);
      setUnitConversions(response.data.items);
    } catch (error) {
      toast.error('Failed to load pending unit conversions');
    } finally {
      setLoadingUnitConversions(false);
    }
  };

  const fetchPendingLcaFactors = async () => {
    setLoadingLcaFactors(true);
    try {
      const response = await axios.get(`${API_URL}/lca-impact-factors?status=pending&page_size=200`);
      setLcaFactors(response.data.items);
    } catch (error) {
      toast.error('Failed to load pending LCA impact factors');
    } finally {
      setLoadingLcaFactors(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const totalPending = factors.length + mappings.length + conversions.length + unitConversions.length + lcaFactors.length;

  // Selection helpers — work against whichever tab is active
  const isMappingsTab = activeTab === 'custom-mappings';
  const isConversionsTab = activeTab === 'category-conversions';
  const isUnitConversionsTab = activeTab === 'unit-conversions';
  const isLcaTab = activeTab === 'lca-impact-factors';
  const lcaCreatedByOptions = useMemo(
    () => [...new Set(lcaFactors.map(factor => factor.user_email).filter(Boolean))].sort().map(email => ({ value: email, label: email })),
    [lcaFactors]
  );
  // created_at is an ISO timestamp, so its first ten characters are the date
  const visibleLcaFactors = useMemo(
    () => lcaFactors.filter(factor => {
      const createdDate = (factor.created_at || '').slice(0, 10);
      return (lcaCreatedByFilter.length === 0 || lcaCreatedByFilter.includes(factor.user_email))
        && (!lcaDateFrom || createdDate >= lcaDateFrom)
        && (!lcaDateTo || createdDate <= lcaDateTo);
    }),
    [lcaFactors, lcaCreatedByFilter, lcaDateFrom, lcaDateTo]
  );
  // A bulk action must never include rows the filters hide
  useEffect(() => {
    const visibleIds = new Set(visibleLcaFactors.map(factor => factor.id));
    setSelectedIds(previous => (isLcaTab && [...previous].some(id => !visibleIds.has(id)) ? new Set([...previous].filter(id => visibleIds.has(id))) : previous));
  }, [visibleLcaFactors, isLcaTab]);
  const lcaFiltersActive = lcaCreatedByFilter.length > 0 || Boolean(lcaDateFrom) || Boolean(lcaDateTo);
  const currentItems = isLcaTab ? visibleLcaFactors : isUnitConversionsTab ? unitConversions : isConversionsTab ? conversions : isMappingsTab ? mappings : factors;
  const itemNoun = isLcaTab ? 'LCA impact factor' : isUnitConversionsTab ? 'unit conversion' : isConversionsTab ? 'category conversion' : isMappingsTab ? 'custom mapping' : 'emission factor';

  const allItemIds = useMemo(
    () => currentItems.map((item) => (isMappingsTab || isConversionsTab || isUnitConversionsTab || isLcaTab ? item.id : item.emission_factor_id)),
    [currentItems, isMappingsTab, isConversionsTab, isUnitConversionsTab, isLcaTab]
  );
  const allSelected = allItemIds.length > 0 && allItemIds.every(id => selectedIds.has(id));
  const someSelected = allItemIds.some(id => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allItemIds));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openBulkDialog = (action) => {
    if (selectedIds.size === 0) {
      toast.error(`Please select at least one ${itemNoun}`);
      return;
    }
    setBulkAction(action);
    setBulkComment('');
    setBulkResults(null);
  };

  const confirmBulkAction = async () => {
    if (!bulkAction) return;
    if (bulkAction === 'reject' && !bulkComment.trim()) {
      toast.error('Please provide a rejection comment');
      return;
    }

    const endpoint = isLcaTab
      ? `${API_URL}/lca-impact-factors/bulk-approve`
      : isUnitConversionsTab
      ? `${API_URL}/unit-conversions/bulk-approve`
      : isConversionsTab
      ? `${API_URL}/category-conversions/bulk-approve`
      : isMappingsTab
      ? `${API_URL}/custom-mappings/bulk-approve`
      : `${API_URL}/emission-factors/bulk-approve`;

    setProcessingBulk(true);
    try {
      const response = await axios.post(endpoint, {
        ids: Array.from(selectedIds),
        action: bulkAction,
        comment: bulkComment.trim() || null,
      });
      const res = response.data;
      setBulkResults(res);

      if (res.failed.length === 0) {
        toast.success(
          `Successfully ${bulkAction === 'approve' ? 'approved' : 'rejected'} ${res.success.length} ${itemNoun}(s)`
        );
      } else if (res.success.length > 0) {
        toast.warning(`${res.success.length} succeeded, ${res.failed.length} failed`);
      } else {
        toast.error(`All ${res.failed.length} operation(s) failed`);
      }

      // New conversions only take effect in the portal after it reloads its maps (once for the whole batch)
      if ((isConversionsTab || isUnitConversionsTab) && bulkAction === 'approve' && res.success.length > 0) reloadPortalStartupMaps();

      // Refresh pending list
      if (isLcaTab) {
        await fetchPendingLcaFactors();
      } else if (isUnitConversionsTab) {
        await fetchPendingUnitConversions();
      } else if (isConversionsTab) {
        await fetchPendingConversions();
      } else if (isMappingsTab) {
        await fetchPendingMappings();
      } else {
        await fetchPendingFactors();
      }
      // Remove processed IDs from selection
      setSelectedIds(prev => {
        const next = new Set(prev);
        res.success.forEach(s => next.delete(s.emission_factor_id ?? s.mapping_id ?? s.conversion_id ?? s.submission_id));
        return next;
      });
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Bulk ${bulkAction} failed: ${msg}`);
    } finally {
      setProcessingBulk(false);
      setBulkAction(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="pending-approvals-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Pending Approvals</h1>
          <p className="text-muted-foreground mt-1">Review and approve items awaiting approval</p>
        </div>

        {/* Summary Card */}
        <Card className="border-gray-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-amber-800">{totalPending}</p>
              <p className="text-sm text-amber-700">
                {factors.length} Emission Factor{factors.length !== 1 ? 's' : ''} + {mappings.length} Custom Mapping{mappings.length !== 1 ? 's' : ''} + {conversions.length} Category Conversion{conversions.length !== 1 ? 's' : ''} + {unitConversions.length} Unit Conversion{unitConversions.length !== 1 ? 's' : ''} + {lcaFactors.length} LCA Impact Factor{lcaFactors.length !== 1 ? 's' : ''} pending review
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Results */}
        {bulkResults && (
          <Card
            className={
              bulkResults.failed.length === 0
                ? 'border-green-200 bg-green-50'
                : bulkResults.success.length === 0
                ? 'border-red-200 bg-red-50'
                : 'border-amber-200 bg-amber-50'
            }
            data-testid="bulk-results"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-600" />
                <div className="flex-1">
                  <div className="font-semibold">
                    Bulk Action Complete — Total: {bulkResults.total}
                  </div>
                  <div className="mt-1 space-x-4">
                    <span className="text-green-700">✓ {bulkResults.success.length} succeeded</span>
                    {bulkResults.failed.length > 0 && (
                      <span className="text-red-600">✗ {bulkResults.failed.length} failed</span>
                    )}
                  </div>
                  {bulkResults.failed.length > 0 && (
                    <div className="mt-3 max-h-48 overflow-y-auto bg-white rounded border border-red-200 p-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-red-100 text-red-800">
                            <th className="p-1 text-left w-24">ID</th>
                            <th className="p-1 text-left">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkResults.failed.map((f, idx) => (
                            <tr key={idx} className="border-t border-red-100">
                              <td className="p-1 font-mono">#{f.emission_factor_id ?? f.mapping_id ?? f.conversion_id ?? f.submission_id}</td>
                              <td className="p-1 text-red-700">{f.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-5xl grid-cols-5 h-auto">
            <TabsTrigger value="emission-factors" className="gap-2 whitespace-normal" data-testid="tab-emission-factors">
              <FileText className="w-4 h-4" />
              Emission Factors ({factors.length})
            </TabsTrigger>
            <TabsTrigger value="custom-mappings" className="gap-2 whitespace-normal" data-testid="tab-custom-mappings">
              <Link2 className="w-4 h-4" />
              Custom Mappings ({mappings.length})
            </TabsTrigger>
            <TabsTrigger value="category-conversions" className="gap-2 whitespace-normal" data-testid="tab-category-conversions">
              <Repeat className="w-4 h-4" />
              Category Conversions ({conversions.length})
            </TabsTrigger>
            <TabsTrigger value="unit-conversions" className="gap-2 whitespace-normal" data-testid="tab-unit-conversions">
              <Ruler className="w-4 h-4" />
              Unit Conversions ({unitConversions.length})
            </TabsTrigger>
            <TabsTrigger value="lca-impact-factors" className="gap-2 whitespace-normal" data-testid="tab-lca-impact-factors">
              <Leaf className="w-4 h-4" />
              LCA Impact Factors ({lcaFactors.length})
            </TabsTrigger>
          </TabsList>

          {/* Emission Factors Tab */}
          <TabsContent value="emission-factors">
            {/* Bulk action toolbar */}
            {canApprove && factors.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 p-3 mb-2 bg-gray-50 border border-gray-200 rounded-md"
                data-testid="bulk-action-toolbar"
              >
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{selectedIds.size}</span> of {factors.length} selected
                  {selectedIds.size > 0 && (
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="ml-3 text-xs text-primary hover:underline"
                    >
                      Clear selection
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('reject')}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="btn-bulk-reject"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject Selected
                  </Button>
                  <Button
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('approve')}
                    className="btn-primary"
                    data-testid="btn-bulk-approve"
                  >
                    {processingBulk ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                    )}
                    Approve Selected
                  </Button>
                </div>
              </div>
            )}

            <Card className="border-gray-200">
              <CardContent className="p-0">
                {loadingFactors ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : factors.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No pending emission factors</p>
                    <p className="text-sm text-muted-foreground mt-1">All emission factors have been reviewed</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          {canApprove && (
                            <TableHead className="table-dense w-10">
                              <Checkbox
                                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                onCheckedChange={toggleAll}
                                aria-label="Select all"
                                data-testid="checkbox-select-all"
                              />
                            </TableHead>
                          )}
                          <TableHead className="table-dense">ID</TableHead>
                          <TableHead className="table-dense">Category</TableHead>
                          <TableHead className="table-dense">Sub Category</TableHead>
                          <TableHead className="table-dense">Type</TableHead>
                          <TableHead className="table-dense">Sub Type</TableHead>
                          <TableHead className="table-dense">Input Unit</TableHead>
                          <TableHead className="table-dense">Country</TableHead>
                          <TableHead className="table-dense">CO2e</TableHead>
                          <TableHead className="table-dense">Created By</TableHead>
                          <TableHead className="table-dense">Created</TableHead>
                          <TableHead className="table-dense text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {factors.map((factor) => {
                          const selected = selectedIds.has(factor.emission_factor_id);
                          const isSiteSpecific = !!(factor.site_identity && String(factor.site_identity).trim());
                          return (
                            <TableRow
                              key={factor.id}
                              className={`table-dense ${selected ? 'bg-primary/5' : isSiteSpecific ? 'bg-blue-50/60 hover:bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                              data-testid={`pending-ef-row-${factor.id}`}
                              data-site-specific={isSiteSpecific ? 'true' : 'false'}
                            >
                              {canApprove && (
                                <TableCell>
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={() => toggleOne(factor.emission_factor_id)}
                                    aria-label={`Select EF ${factor.emission_factor_id}`}
                                    data-testid={`checkbox-ef-${factor.emission_factor_id}`}
                                  />
                                </TableCell>
                              )}
                              <TableCell className="font-mono text-sm">
                                <div className="flex flex-col items-start gap-1">
                                  <span>{factor.emission_factor_id}</span>
                                  {isSiteSpecific && (
                                    <span
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-300"
                                      title={`Site-specific factor (${factor.site_identity})`}
                                      data-testid={`site-badge-${factor.id}`}
                                    >
                                      Site
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{getCategoryLabel(factor.category)}</TableCell>
                              <TableCell>{factor.sub_category ?? ''}</TableCell>
                              <TableCell>{factor.type ?? ''}</TableCell>
                              <TableCell>{factor.sub_type ?? ''}</TableCell>
                              <TableCell>{factor.input_unit || '—'}</TableCell>
                              <TableCell>{getCountryName(factor.country)}</TableCell>
                              <TableCell>{formatFactorValue(factor.co2e)}</TableCell>
                              <TableCell className="text-sm">{factor.created_by}</TableCell>
                              <TableCell>{formatDate(factor.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/emission-factors/${factor.emission_factor_id}?from=pending`)}
                                  title="View & Review"
                                  data-testid={`view-pending-ef-${factor.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Custom Mappings Tab */}
          <TabsContent value="custom-mappings">
            {/* Bulk action toolbar */}
            {canApprove && mappings.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 p-3 mb-2 bg-gray-50 border border-gray-200 rounded-md"
                data-testid="bulk-action-toolbar-mappings"
              >
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{selectedIds.size}</span> of {mappings.length} selected
                  {selectedIds.size > 0 && (
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="ml-3 text-xs text-primary hover:underline"
                    >
                      Clear selection
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('reject')}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="btn-bulk-reject-mappings"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject Selected
                  </Button>
                  <Button
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('approve')}
                    className="btn-primary"
                    data-testid="btn-bulk-approve-mappings"
                  >
                    {processingBulk ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                    )}
                    Approve Selected
                  </Button>
                </div>
              </div>
            )}

            <Card className="border-gray-200">
              <CardContent className="p-0">
                {loadingMappings ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : mappings.length === 0 ? (
                  <div className="text-center py-12">
                    <Link2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No pending custom mappings</p>
                    <p className="text-sm text-muted-foreground mt-1">All custom mappings have been reviewed</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          {canApprove && (
                            <TableHead className="table-dense w-10">
                              <Checkbox
                                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                onCheckedChange={toggleAll}
                                aria-label="Select all"
                                data-testid="checkbox-select-all-mappings"
                              />
                            </TableHead>
                          )}
                          <TableHead className="table-dense">ID</TableHead>
                          <TableHead className="table-dense">Custom Category</TableHead>
                          <TableHead className="table-dense">Custom Sub Category</TableHead>
                          <TableHead className="table-dense">Custom Type</TableHead>
                          <TableHead className="table-dense">Custom Sub Type</TableHead>
                          <TableHead className="table-dense">Additional Information</TableHead>
                          <TableHead className="table-dense">Linked EF ID</TableHead>
                          <TableHead className="table-dense">EF Category</TableHead>
                          <TableHead className="table-dense">EF CO2e</TableHead>
                          <TableHead className="table-dense">Created By</TableHead>
                          <TableHead className="table-dense">Created</TableHead>
                          <TableHead className="table-dense text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mappings.map((mapping) => {
                          const selected = selectedIds.has(mapping.id);
                          return (
                          <TableRow
                            key={mapping.id}
                            className={`table-dense ${selected ? 'bg-primary/5' : ''}`}
                            data-testid={`pending-mapping-row-${mapping.id}`}
                          >
                            {canApprove && (
                              <TableCell>
                                <Checkbox
                                  checked={selected}
                                  onCheckedChange={() => toggleOne(mapping.id)}
                                  aria-label={`Select custom mapping ${mapping.id}`}
                                  data-testid={`checkbox-mapping-${mapping.id}`}
                                />
                              </TableCell>
                            )}
                            <TableCell className="font-mono text-sm">{mapping.id}</TableCell>
                            <TableCell className="font-medium">{getCategoryLabel(mapping.category)}</TableCell>
                            <TableCell>{mapping.sub_category}</TableCell>
                            <TableCell>{mapping.type || '—'}</TableCell>
                            <TableCell>{mapping.sub_type || '—'}</TableCell>
                            <TableCell className="max-w-[260px] truncate" title={mapping.additional_information || ''}>
                              {mapping.additional_information || '—'}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              <span className="text-primary">#{mapping.desired_emission_factor_id}</span>
                            </TableCell>
                            <TableCell>{getCategoryLabel(mapping.ef_category)}</TableCell>
                            <TableCell>{formatFactorValue(mapping.ef_co2e)}</TableCell>
                            <TableCell className="text-sm">{mapping.created_by}</TableCell>
                            <TableCell>{formatDate(mapping.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/custom-mappings/${mapping.id}?from=pending`)}
                                title="View & Review"
                                data-testid={`view-pending-mapping-${mapping.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Category Conversions Tab */}
          <TabsContent value="category-conversions">
            {canApprove && conversions.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 p-3 mb-2 bg-gray-50 border border-gray-200 rounded-md"
                data-testid="bulk-action-toolbar-conversions"
              >
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{selectedIds.size}</span> of {conversions.length} selected
                  {selectedIds.size > 0 && (
                    <button onClick={() => setSelectedIds(new Set())} className="ml-3 text-xs text-primary hover:underline">
                      Clear selection
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('reject')}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="btn-bulk-reject-conversions"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject Selected
                  </Button>
                  <Button
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('approve')}
                    className="btn-primary"
                    data-testid="btn-bulk-approve-conversions"
                  >
                    {processingBulk ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Approve Selected
                  </Button>
                </div>
              </div>
            )}

            <Card className="border-gray-200">
              <CardContent className="p-0">
                {loadingConversions ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : conversions.length === 0 ? (
                  <div className="text-center py-12">
                    <Repeat className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No pending category conversions</p>
                    <p className="text-sm text-muted-foreground mt-1">All category conversions have been reviewed</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          {canApprove && (
                            <TableHead className="table-dense w-10">
                              <Checkbox
                                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                onCheckedChange={toggleAll}
                                aria-label="Select all"
                                data-testid="checkbox-select-all-conversions"
                              />
                            </TableHead>
                          )}
                          <TableHead className="table-dense">ID</TableHead>
                          <TableHead className="table-dense">Sub Category</TableHead>
                          <TableHead className="table-dense">Type</TableHead>
                          <TableHead className="table-dense">Sub Type</TableHead>
                          <TableHead className="table-dense">From</TableHead>
                          <TableHead className="table-dense">To</TableHead>
                          <TableHead className="table-dense">Value</TableHead>
                          <TableHead className="table-dense">Created By</TableHead>
                          <TableHead className="table-dense">Created</TableHead>
                          <TableHead className="table-dense text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {conversions.map((conversion) => {
                          const selected = selectedIds.has(conversion.id);
                          return (
                            <TableRow
                              key={conversion.id}
                              className={`table-dense ${selected ? 'bg-primary/5' : ''}`}
                              data-testid={`pending-conversion-row-${conversion.id}`}
                            >
                              {canApprove && (
                                <TableCell>
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={() => toggleOne(conversion.id)}
                                    aria-label={`Select category conversion ${conversion.id}`}
                                    data-testid={`checkbox-conversion-${conversion.id}`}
                                  />
                                </TableCell>
                              )}
                              <TableCell className="font-mono text-sm">{conversion.id}</TableCell>
                              <TableCell className="font-medium">{conversion.conversion_sub_category}</TableCell>
                              <TableCell>{conversion.conversion_type}</TableCell>
                              <TableCell>{conversion.conversion_sub_type}</TableCell>
                              <TableCell>{conversion.conversion_from}</TableCell>
                              <TableCell>{conversion.conversion_to}</TableCell>
                              <TableCell className="font-mono text-sm">{conversion.value}</TableCell>
                              <TableCell className="text-sm">{conversion.user_email}</TableCell>
                              <TableCell>{formatDate(conversion.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/category-conversions/${conversion.id}?from=pending`)}
                                  title="View & Review"
                                  data-testid={`view-pending-conversion-${conversion.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unit Conversions Tab */}
          <TabsContent value="unit-conversions">
            {canApprove && unitConversions.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 p-3 mb-2 bg-gray-50 border border-gray-200 rounded-md"
                data-testid="bulk-action-toolbar-unit-conversions"
              >
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{selectedIds.size}</span> of {unitConversions.length} selected
                  {selectedIds.size > 0 && (
                    <button onClick={() => setSelectedIds(new Set())} className="ml-3 text-xs text-primary hover:underline">
                      Clear selection
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('reject')}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="btn-bulk-reject-unit-conversions"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject Selected
                  </Button>
                  <Button
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('approve')}
                    className="btn-primary"
                    data-testid="btn-bulk-approve-unit-conversions"
                  >
                    {processingBulk ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Approve Selected
                  </Button>
                </div>
              </div>
            )}

            <Card className="border-gray-200">
              <CardContent className="p-0">
                {loadingUnitConversions ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : unitConversions.length === 0 ? (
                  <div className="text-center py-12">
                    <Ruler className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No pending unit conversions</p>
                    <p className="text-sm text-muted-foreground mt-1">All unit conversions have been reviewed</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          {canApprove && (
                            <TableHead className="table-dense w-10">
                              <Checkbox
                                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                onCheckedChange={toggleAll}
                                aria-label="Select all"
                                data-testid="checkbox-select-all-unit-conversions"
                              />
                            </TableHead>
                          )}
                          <TableHead className="table-dense">Unit Category</TableHead>
                          <TableHead className="table-dense">From</TableHead>
                          <TableHead className="table-dense">To</TableHead>
                          <TableHead className="table-dense">Value</TableHead>
                          <TableHead className="table-dense">Created By</TableHead>
                          <TableHead className="table-dense">Created</TableHead>
                          <TableHead className="table-dense text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unitConversions.map((conversion) => {
                          const selected = selectedIds.has(conversion.id);
                          return (
                            <TableRow
                              key={conversion.id}
                              className={`table-dense ${selected ? 'bg-primary/5' : ''}`}
                              data-testid={`pending-unit-conversion-row-${conversion.id}`}
                            >
                              {canApprove && (
                                <TableCell>
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={() => toggleOne(conversion.id)}
                                    aria-label={`Select unit conversion ${conversion.id}`}
                                    data-testid={`checkbox-unit-conversion-${conversion.id}`}
                                  />
                                </TableCell>
                              )}
                              <TableCell>{conversion.unit_category || '—'}</TableCell>
                              <TableCell className="font-medium">{conversion.conversion_from}</TableCell>
                              <TableCell className="font-medium">{conversion.conversion_to}</TableCell>
                              <TableCell className="font-mono text-sm">{conversion.value}</TableCell>
                              <TableCell className="text-sm">{conversion.user_email}</TableCell>
                              <TableCell>{formatDate(conversion.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/unit-conversions/${conversion.id}?from=pending`)}
                                  title="View & Review"
                                  data-testid={`view-pending-unit-conversion-${conversion.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          {/* LCA Impact Factors Tab */}
          <TabsContent value="lca-impact-factors">
            {canApprove && lcaFactors.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 p-3 mb-2 bg-gray-50 border border-gray-200 rounded-md"
                data-testid="bulk-action-toolbar-lca"
              >
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{selectedIds.size}</span> of {visibleLcaFactors.length} selected
                  {selectedIds.size > 0 && (
                    <button onClick={() => setSelectedIds(new Set())} className="ml-3 text-xs text-primary hover:underline">
                      Clear selection
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('reject')}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="btn-bulk-reject-lca"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject Selected
                  </Button>
                  <Button
                    size="sm"
                    disabled={selectedIds.size === 0 || processingBulk}
                    onClick={() => openBulkDialog('approve')}
                    className="btn-primary"
                    data-testid="btn-bulk-approve-lca"
                  >
                    {processingBulk ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Approve Selected
                  </Button>
                </div>
              </div>
            )}

            {lcaFactors.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="lca-pending-filters">
                <span className="text-sm text-muted-foreground mr-1">Filters:</span>
                <MultiSelectFilter label="Created By" options={lcaCreatedByOptions} selected={lcaCreatedByFilter} onSelectionChange={setLcaCreatedByFilter} testIdPrefix="lca-created-by-" />
                <span className="text-sm text-muted-foreground ml-2">Created</span>
                <Input type="date" value={lcaDateFrom} max={lcaDateTo || undefined} onChange={(e) => setLcaDateFrom(e.target.value)} className="h-8 w-40" aria-label="Created from" data-testid="lca-date-from" />
                <span className="text-sm text-muted-foreground">to</span>
                <Input type="date" value={lcaDateTo} min={lcaDateFrom || undefined} onChange={(e) => setLcaDateTo(e.target.value)} className="h-8 w-40" aria-label="Created to" data-testid="lca-date-to" />
                {lcaFiltersActive && (
                  <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => { setLcaCreatedByFilter([]); setLcaDateFrom(''); setLcaDateTo(''); }} data-testid="lca-clear-filters">
                    <X className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                )}
                <span className="text-sm text-muted-foreground ml-auto">{visibleLcaFactors.length} of {lcaFactors.length}</span>
              </div>
            )}
            <Card className="border-gray-200">
              <CardContent className="p-0">
                {loadingLcaFactors ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : lcaFactors.length === 0 ? (
                  <div className="text-center py-12">
                    <Leaf className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No pending LCA impact factors</p>
                    <p className="text-sm text-muted-foreground mt-1">All LCA impact factors have been reviewed</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          {canApprove && (
                            <TableHead className="table-dense w-10">
                              <Checkbox
                                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                onCheckedChange={toggleAll}
                                aria-label="Select all"
                                data-testid="checkbox-select-all-lca"
                              />
                            </TableHead>
                          )}
                          <TableHead className="table-dense">IFID</TableHead>
                          <TableHead className="table-dense">Category</TableHead>
                          <TableHead className="table-dense">Method</TableHead>
                          <TableHead className="table-dense">Source Database</TableHead>
                          <TableHead className="table-dense">Unit</TableHead>
                          <TableHead className="table-dense">Created By</TableHead>
                          <TableHead className="table-dense">Created</TableHead>
                          <TableHead className="table-dense text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleLcaFactors.length === 0 && (
                          <TableRow><TableCell colSpan={canApprove ? 9 : 8} className="text-center text-muted-foreground py-6">No pending LCA impact factors match the filters</TableCell></TableRow>
                        )}
                        {visibleLcaFactors.map((factor) => {
                          const selected = selectedIds.has(factor.id);
                          return (
                            <TableRow
                              key={factor.id}
                              className={`table-dense ${selected ? 'bg-primary/5' : ''}`}
                              data-testid={`pending-lca-row-${factor.id}`}
                            >
                              {canApprove && (
                                <TableCell>
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={() => toggleOne(factor.id)}
                                    aria-label={`Select LCA impact factor ${factor.id}`}
                                    data-testid={`checkbox-lca-${factor.id}`}
                                  />
                                </TableCell>
                              )}
                              <TableCell className="font-mono text-sm">{factor.emission_factor_impact_id ?? 'New'}</TableCell>
                              <TableCell className="text-sm">{[factor.category, factor.sub_category, factor.type, factor.sub_type].filter(Boolean).join(' / ')}</TableCell>
                              <TableCell className="font-medium">{factor.methodology}{factor.is_update && <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">Update</span>}</TableCell>
                              <TableCell>{factor.source_database || '—'}</TableCell>
                              <TableCell>{factor.input_unit || '—'}</TableCell>
                              <TableCell className="text-sm">{factor.user_email}</TableCell>
                              <TableCell>{formatDate(factor.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/lca-impact-factors/${factor.id}?from=pending`)}
                                  title="View & Review"
                                  data-testid={`view-pending-lca-${factor.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bulk action confirmation dialog */}
        <AlertDialog open={!!bulkAction} onOpenChange={(open) => !open && setBulkAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {bulkAction === 'approve' ? 'Approve' : 'Reject'} {selectedIds.size} {isLcaTab ? 'LCA Impact Factor' : isUnitConversionsTab ? 'Unit Conversion' : isConversionsTab ? 'Category Conversion' : isMappingsTab ? 'Custom Mapping' : 'Emission Factor'}{selectedIds.size !== 1 ? 's' : ''}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {bulkAction === 'approve' ? (
                  <>
                    You are about to approve <strong>{selectedIds.size}</strong> {itemNoun}(s).
                    They will be moved to the main table and synced to production.
                    The editors will be notified by email.
                  </>
                ) : (
                  <>
                    You are about to reject <strong>{selectedIds.size}</strong> {itemNoun}(s).
                    Please provide a common rejection reason below. The editors will be notified by email.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="py-2">
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                {bulkAction === 'approve' ? 'Comment (optional)' : 'Rejection reason (required)'}
              </label>
              <Textarea
                value={bulkComment}
                onChange={(e) => setBulkComment(e.target.value)}
                placeholder={
                  bulkAction === 'approve'
                    ? 'Optional comment about this bulk approval'
                    : `Explain why these ${itemNoun}s are being rejected…`
                }
                rows={3}
                disabled={processingBulk}
                data-testid="bulk-comment-textarea"
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={processingBulk}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmBulkAction}
                disabled={processingBulk}
                className={bulkAction === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'btn-primary'}
                data-testid="btn-confirm-bulk"
              >
                {processingBulk ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : bulkAction === 'approve' ? (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                {bulkAction === 'approve' ? 'Approve All' : 'Reject All'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
